<script setup lang="ts">
import { type AxiosError } from 'axios';
import PaymentLinkTerms from './PaymentLinkTerms.vue';
import PaymentLinkResult from './PaymentLinkResult.vue';
import regex from '@static/regex';
import { PaymentsApiApiBillsBeginPaymentByPaymentLinkPost } from '@clients/BillApi';
import type TripPackage from '@itinerary/trip/TripPackage';

const props = defineProps<{
    disabled?: boolean;
}>();

const attemptsConfigs = {
    Threshold: 5,
    dailyLimit: 3,
};

const { open } = useModal({
    component: PaymentLinkTerms,
    modalAttrs: {
        zIndexFn: () => 9999999,
    },
});

const { open: openSuccess } = useModal({
    component: PaymentLinkResult,
    modalAttrs: {
        zIndexFn: () => 9999999,
    },
});

const invoiceStore = useInvoiceStore();
const authStore = useAuthStore();
const analyticsStore = useAnalyticsStore();
const packageSelectStore = usePackageSelectStore();

const target = ref<HTMLDivElement>();

const { stop } = useIntersectionObserver(
    target,
    ([entry]) => {
        if (entry?.isIntersecting) {
            analyticsStore.trackAll(
                'pay_by_others_component_viewed',
                eventMetric.value
            );
            // Optional: stop observing if it should only run once
            stop();
        }
    },
    {
        threshold: 0.1, // trigger once 10% of the element is visible
    }
);

enum AttemptLimitValidationStatus {
    daily = 'daily-limit',
    threshold = 'threshold-limit',
    valid = 'no-limit',
}

const validationErrorMessage = ref<string>();
const validationStatus = ref<AttemptLimitValidationStatus>(
    AttemptLimitValidationStatus.valid
);
const mobileNumber = ref('');
const isDirty = ref(false);
const mobileNumberError = computed(() => {
    if (!validateMobileNumber.value && isDirty.value) {
        return 'شماره موبایل وارد شده معتبر نمی‌باشد.';
    }
    return false;
});
const loading = defineModel<boolean>('loading', { default: false });

const tryAttempts = useLocalStorage('payment-links-tries', {
    attempts: 0,
    date: new Date().getTime(),
});

const eventMetric = computed(() => {
    const pkg = packageSelectStore.selectedPackages?.[0] as TripPackage;
    return {
        ...authStore.eventMetrics,
        ...invoiceStore.eventMetric,
        tripType: 'flight_i',
        from: pkg.origin,
        to: pkg.destination,
        departureDate: pkg.departureTime,
        toDate: pkg.arrivalDateTime,
    };
});

watch(mobileNumber, (newValue, oldValue) => {
    const input = convertPersianNumbers(newValue);
    const partialValid = regex.mobile.partial.test(input);
    if (!partialValid && input.length) {
        nextTick(() => {
            mobileNumber.value = oldValue;
        });
    }
});

const validate = () => {
    const sameDay = dayConfig
        .js()
        .isSame(dayConfig.js(tryAttempts.value.date), 'day');
    if (!sameDay) {
        tryAttempts.value = {
            attempts: 0,
            date: Date.now(),
        };
    }
    if (tryAttempts.value.attempts) {
        if (!(tryAttempts.value.attempts < attemptsConfigs.dailyLimit)) {
            validationStatus.value = AttemptLimitValidationStatus.daily;
            return AttemptLimitValidationStatus.daily;
        } else if (
            !dayConfig
                .js()
                .isSameOrAfter(
                    dayConfig
                        .js(tryAttempts.value.date)
                        .add(attemptsConfigs.Threshold, 'minutes')
                )
        ) {
            validationStatus.value = AttemptLimitValidationStatus.threshold;
            return AttemptLimitValidationStatus.threshold;
        }
    }
    validationStatus.value = AttemptLimitValidationStatus.valid;
    return AttemptLimitValidationStatus.valid;
};

const validateMobileNumber = computed(() => {
    const input = convertPersianNumbers(mobileNumber.value.trim());
    return regex.mobile.full.test(input);
});

const thresholdUpdate = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');

    return `ارسال دوباره تا ${mm}:${ss}`;
};

const thresholdHandler = () => {
    const val = validate();
    if (val === AttemptLimitValidationStatus.threshold) {
        const mms =
            dayConfig
                .js(tryAttempts.value.date)
                .add(attemptsConfigs.Threshold, 'minute')
                .valueOf() - new Date().getTime();
        validationErrorMessage.value = thresholdUpdate(mms);
    } else if (val === AttemptLimitValidationStatus.daily) {
        validationErrorMessage.value = 'سقف مجاز استفاده';
    } else if (val === AttemptLimitValidationStatus.valid) {
        validationErrorMessage.value = 'ارسال لینک';
    }
};

const validator = () => {
    const res = validate();

    switch (res) {
        case AttemptLimitValidationStatus.daily:
            validationErrorMessage.value = 'سقف مجاز استفاده';
            return false;
        case AttemptLimitValidationStatus.threshold:
            thresholdHandler();
            return false;
        default:
            validationErrorMessage.value = 'ارسال لینک';
            return true;
    }
};

const submit = async () => {
    if (props.disabled || loading.value) {
        return;
    }
    analyticsStore.trackAll('pay_by_others_paylink_clicked', eventMetric.value);
    if (!validateMobileNumber.value) {
        return;
    }
    const val = validator();
    if (val) {
        try {
            loading.value = true;
            const { billCode } = invoiceStore;
            const { data } =
                await PaymentsApiApiBillsBeginPaymentByPaymentLinkPost({
                    beginPaymentByPaymentLinkRequestDto: {
                        BillCode: billCode,
                        Mobile: mobileNumber.value,
                    },
                });
            if (data.IsSuccess) {
                invoiceStore.paymentLinkSentForBillCode = billCode;
                invoiceStore.saveSession();
                localStorage.setItem(
                    'after_order_event_parameter',
                    JSON.stringify({
                        ...analyticsStore.searchResultsAttributesForReserve,
                    })
                );
                openSuccess({
                    state: 'succeed',
                    trackingId: data.TrackingId ?? '',
                    mobileNumber: mobileNumber.value,
                });
                analyticsStore.trackAll(
                    'pay_by_others_confirmation_viewed',
                    eventMetric.value
                );
                tryAttempts.value = {
                    attempts: tryAttempts.value.attempts + 1,
                    date: new Date().getTime(),
                };
                validator();
            } else {
                analyticsStore.trackAll(
                    'pay_by_others_link_send_failed',
                    eventMetric.value
                );
                openSuccess({
                    state: 'failed',
                    errorMessage: data.ErrorMessage ?? undefined,
                    mobileNumber: mobileNumber.value,
                });
            }
        } catch (error) {
            analyticsStore.trackAll(
                'pay_by_others_link_send_failed',
                eventMetric.value
            );
            const err = error as AxiosError;
            const data = err?.response?.data as any;
            const errorMessage =
                typeof data === 'string'
                    ? data
                    : data?.error ||
                      data?.Error ||
                      data?.message ||
                      data?.Message ||
                      data?.ErrorMessage ||
                      data?.errorMessage;
            openSuccess({
                state: 'failed',
                errorMessage,
                mobileNumber: mobileNumber.value,
            });
        } finally {
            loading.value = false;
        }
    }
};

const openTerms = () => {
    analyticsStore.trackAll('pay_by_others_help_clicked', eventMetric.value);
    open();
};

const linkDisabled = computed(
    () => validationStatus.value !== AttemptLimitValidationStatus.valid
);

const { isMobile } = useSize();
function onClick() {
    if (isMobile.value) {
        setTimeout(() => {
            target.value?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 500);
    }
}

onMounted(thresholdHandler);
useIntervalFn(thresholdHandler, 1000);
</script>

<template>
    <div id="payment-link" ref="target" class="payment-link">
        <div class="payment-information">
            <mr-typography size="x-large" weight="700" color="var(--gray-900)">
                ارسال لینک پرداخت برای دیگری
            </mr-typography>
            <mr-typography color="var(--gray-700)">
                شماره موبایل فرد پرداخت‌کننده را وارد کنید. لینک پرداخت به این
                شماره، ارسال خواهد شد.
            </mr-typography>
        </div>
        <mr-input
            v-model="mobileNumber"
            :disabled="loading"
            placeholder="شماره موبایل"
            :hide-description="!mobileNumberError"
            :error-text="mobileNumberError ? mobileNumberError : ''"
            :invalid="!!mobileNumberError"
            required
            type="number"
            @click="onClick"
            @blur="isDirty = true"
            @update:model-value="isDirty = false"
        />
        <div class="payment-actions">
            <mr-button
                variant="transparent"
                full-width
                :loading
                @click="openTerms"
            >
                راهنمای ارسال
            </mr-button>
            <mr-button
                variant="bordered"
                :disabled="disabled || !validateMobileNumber || linkDisabled"
                :loading
                full-width
                @click="submit"
            >
                {{ validationErrorMessage }}
            </mr-button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.payment-link {
    display: flex;
    flex-direction: column;
    gap: 24px;
    background-color: white;
    border-radius: 16px;
    padding: 12px;
    @include shadow();
    .payment-information {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .payment-actions {
        display: flex;
        gap: 12px;
        align-items: center;
    }
}
</style>
